import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import LoginForm from './components/LoginForm';
import TrustBadges from './components/TrustBadges';
import RegistrationLinks from './components/RegistrationLinks';
import workerProfileService from '../../services/workerProfileService';

const Login = () => {
  const [searchParams] = useSearchParams();
  const [workerProfiles, setWorkerProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const serviceArea = searchParams?.get('serviceArea');

  useEffect(() => {
    const fetchWorkerProfiles = async () => {
      if (!serviceArea) return;
      setLoading(true);
      try {
        const profiles = await workerProfileService?.getRecentWorkerProfilesByServiceArea(serviceArea);
        setWorkerProfiles(profiles || []);
      } catch (error) {
        console.error('Error fetching worker profiles:', error);
        setWorkerProfiles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkerProfiles();
  }, [serviceArea]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      {/* Subtle glow */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-[160px] opacity-5 pointer-events-none" style={{ background: '#00E5A0' }} />
      <div className="w-full max-w-6xl relative z-10">
        {/* Worker Profiles Section */}
        {serviceArea && (
          <div className="mb-10">
            <div className="text-center mb-8">
              <p className="text-xs font-body font-medium tracking-[0.2em] uppercase mb-3" style={{ color: '#00E5A0' }}>— Beschikbare specialisten</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">
                {serviceArea}
              </h2>
              <p className="text-sm" style={{ color: '#666666' }}>
                Log in om volledige profielinformatie en contactgegevens te bekijken
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-8 h-8 rounded-full border-2 animate-spin" style={{ borderColor: '#1A1A1A', borderTopColor: '#00E5A0' }}></div>
              </div>
            ) : workerProfiles?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mb-10" style={{ background: '#1A1A1A' }}>
                {workerProfiles?.map((profile, index) => (
                  <div
                    key={profile?.id}
                    className="p-6"
                    style={{ background: '#0A0A0A' }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-body font-semibold text-white mb-1">Specialist #{index + 1}</h3>
                        <p className="text-xs" style={{ color: '#555555' }}>Locatie: ••••••</p>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 rounded-sm" style={{ background: 'rgba(0,229,160,0.1)' }}>
                        <Icon name="Star" size={13} color="#00E5A0" />
                        <span className="text-xs font-body font-semibold" style={{ color: '#00E5A0' }}>{profile?.rating || '5.0'}</span>
                      </div>
                    </div>
                    <p className="text-sm mb-4" style={{ color: '#888888' }}>{profile?.specialization}</p>
                    <div className="flex items-center justify-between text-xs mb-5" style={{ color: '#555555' }}>
                      <span>Ervaring: ••••</span>
                      <span className="font-semibold" style={{ color: '#444444' }}>Tarief: ••••</span>
                    </div>
                    <div className="pt-4 flex items-center gap-2 text-xs" style={{ borderTop: '1px solid #1A1A1A', color: '#555555' }}>
                      <Icon name="Lock" size={13} color="#555555" />
                      <span>Log in om volledige details te zien</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 mb-10" style={{ border: '1px solid #1A1A1A' }}>
                <p style={{ color: '#666666' }}>Geen profielen beschikbaar voor dit vakgebied</p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left side */}
          <div className="order-2 lg:order-1 space-y-8">
            <div className="text-center lg:text-left">
              <Link to="/" className="inline-flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity">
                <img src="/assets/images/Untitled-1774116510083.jpeg" alt="BVM Group Logo" className="h-12 w-auto object-contain" />
                <span className="font-heading font-bold text-xl text-white">BVM Group</span>
              </Link>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
                {serviceArea ? 'Log in om contact op te nemen' : 'Welkom terug'}
              </h1>
              <p className="text-base" style={{ color: '#888888' }}>
                {serviceArea
                  ? 'Maak contact met specialisten en bekijk hun volledige profielen' :'Log in op uw account om door te gaan met het platform'
                }
              </p>
            </div>

            <TrustBadges />

            <div className="hidden lg:block space-y-4">
              <h3 className="text-sm font-body font-semibold tracking-widest uppercase" style={{ color: '#555555' }}>Waarom BVM Group?</h3>
              <ul className="space-y-3">
                {[
                  'Directe verbinding tussen opdrachtgevers en installateurs',
                  'Transparant commissiesysteem voor affiliates',
                  'Veilige betalingen en GDPR-compliant',
                  'Real-time opdracht tracking en communicatie'
                ]?.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(0,229,160,0.1)' }}>
                      <Icon name="Check" size={12} color="#00E5A0" />
                    </div>
                    <span className="text-sm" style={{ color: '#888888' }}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right side - Login form */}
          <div className="order-1 lg:order-2">
            <div className="p-8 md:p-10" style={{ background: '#0D0D0D', border: '1px solid #1A1A1A' }}>
              <LoginForm />
              <div className="mt-8 pt-8" style={{ borderTop: '1px solid #1A1A1A' }}>
                <RegistrationLinks />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-xs" style={{ color: '#444444' }}>
            © {new Date()?.getFullYear()} BVM Group. Alle rechten voorbehouden.
          </p>
          <div className="flex items-center justify-center gap-4 mt-3">
            {['Privacy Policy', 'Algemene Voorwaarden', 'Ondersteuning']?.map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span style={{ color: '#333333' }}>·</span>}
                <Link to="/login" className="text-xs transition-colors hover:text-white" style={{ color: '#444444' }}>{l}</Link>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;